import { withAlpha } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { MouseEventArgs } from "../core/events.js";
import { clamp, type Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import { paintGlyph } from "../themes/glyphRegistry.js";
import type { Theme } from "../themes/Theme.js";
import type { ItemSprite } from "./items.js";

export interface QueueEntry {
  id: string;
  name: string;
  sprite?: ItemSprite | null;
  glyph?: string;
  /** How long the whole job takes, in seconds. */
  seconds: number;
  /** How much of it is done, in seconds. Only the head of the queue advances. */
  elapsed?: number;
  /** Several of the same thing queued behind each other. */
  count?: number;
}

export interface BuildQueueOptions extends ControlOptions {
  entries?: readonly QueueEntry[];
  /** Side of one queue cell. */
  cellSize?: number;
  gap?: number;
  vertical?: boolean;
  /** Right-click, or click the cross, to cancel a job. */
  cancellable?: boolean;
  font?: FontSpec | null;
  onCancel?: (entry: QueueEntry, index: number) => void;
  onComplete?: (entry: QueueEntry, index: number) => void;
}

const DEFAULT_CELL = 44;
const DEFAULT_GAP = 4;

/**
 * The production queue: what is being built, what is waiting, and how far the
 * first one has got.
 *
 * Only the head of the queue advances — one thing is built at a time, which
 * is what a queue *is* — and `tick` moves it, finishes it and pulls the next
 * one up. The caller drives the clock, so a paused game has a paused queue.
 *
 * The progress on the head is drawn as an arc, which is the shape the genre
 * uses and which the renderer can now do in one instance.
 */
export class BuildQueue extends Control {
  #entries: QueueEntry[];
  #hovered = -1;

  cellSize: number;
  gap: number;
  vertical: boolean;
  cancellable: boolean;
  font: FontSpec | null;

  constructor(options: BuildQueueOptions = {}) {
    super({ width: 300, height: 48, tabStop: false, ...options });
    this.#entries = [...(options.entries ?? [])];
    this.cellSize = options.cellSize ?? DEFAULT_CELL;
    this.gap = options.gap ?? DEFAULT_GAP;
    this.vertical = options.vertical ?? false;
    this.cancellable = options.cancellable ?? true;
    this.font = options.font ?? null;
    if (options.onCancel) {
      this.on("itemactivate", (index) => {
        const entry = this.#lastCanceled;
        if (entry) options.onCancel!(entry, index);
      });
    }
    if (options.onComplete) this.on("change", () => {
      const done = this.#lastCompleted;
      if (done) options.onComplete!(done.entry, done.index);
    });

    this.on("pointermove", (e) => this.#hover(e));
    this.on("pointerleave", () => this.#setHovered(-1));
    this.on("contextmenu", (e) => this.#onContextMenu(e));
  }

  #lastCanceled: QueueEntry | null = null;
  #lastCompleted: { entry: QueueEntry; index: number } | null = null;

  get entries(): readonly QueueEntry[] {
    return this.#entries;
  }
  set entries(v: readonly QueueEntry[]) {
    this.#entries = [...v];
    this.emit("change");
    this.invalidate();
  }

  /** The job being worked on, or null when the queue is empty. */
  get head(): QueueEntry | null {
    return this.#entries[0] ?? null;
  }

  /** How far the head has got, 0..1. */
  get headProgress(): number {
    const head = this.head;
    if (!head || head.seconds <= 0) return 0;
    return clamp((head.elapsed ?? 0) / head.seconds, 0, 1);
  }

  /** Seconds until the whole queue is finished. */
  get totalSeconds(): number {
    return this.#entries.reduce(
      (total, e) => total + (e.seconds * (e.count ?? 1) - (e.elapsed ?? 0)),
      0,
    );
  }

  enqueue(entry: QueueEntry): void {
    this.#entries.push(entry);
    this.emit("change");
    this.invalidate();
  }

  /** Take a job out of the queue. */
  cancel(index: number): QueueEntry | null {
    const entry = this.#entries[index];
    if (!entry || !this.cancellable) return null;
    // Canceling one of a batch takes one off the count rather than the lot:
    // queueing five and wanting four is far commoner than wanting none.
    if ((entry.count ?? 1) > 1) {
      this.#entries[index] = { ...entry, count: (entry.count ?? 1) - 1 };
    } else {
      this.#entries.splice(index, 1);
    }
    this.#lastCanceled = entry;
    this.emit("itemactivate", index);
    this.emit("change");
    this.invalidate();
    return entry;
  }

  clear(): void {
    this.#entries.length = 0;
    this.emit("change");
    this.invalidate();
  }

  /**
   * Advance the head of the queue by `deltaSeconds`, finishing jobs and
   * pulling the next one up as it goes.
   *
   * Returns everything that finished this tick, which will be more than one
   * item if a big delta swallowed several short jobs.
   */
  tick(deltaSeconds: number): QueueEntry[] {
    const finished: QueueEntry[] = [];
    let left = deltaSeconds;
    while (left > 0 && this.#entries.length > 0) {
      const head = this.#entries[0]!;
      const elapsed = (head.elapsed ?? 0) + left;
      if (elapsed < head.seconds) {
        this.#entries[0] = { ...head, elapsed };
        left = 0;
        break;
      }
      // This one is done; whatever time is over rolls into the next.
      left = elapsed - head.seconds;
      finished.push(head);
      this.#lastCompleted = { entry: head, index: 0 };
      if ((head.count ?? 1) > 1) {
        this.#entries[0] = { ...head, count: (head.count ?? 1) - 1, elapsed: 0 };
      } else {
        this.#entries.shift();
      }
    }
    if (finished.length > 0) this.emit("change");
    this.invalidate();
    return finished;
  }

  /** The queue cell containing a local point, or -1. */
  indexAt(x: number, y: number): number {
    const stride = this.cellSize + this.gap;
    const along = this.vertical ? y : x;
    const across = this.vertical ? x : y;
    if (across < 0 || across > this.cellSize) return -1;
    const index = Math.floor(along / stride);
    return index >= 0 && index < this.#entries.length ? index : -1;
  }

  #hover(e: MouseEventArgs): void {
    this.#setHovered(this.indexAt(e.x, e.y));
  }

  #setHovered(index: number): void {
    if (index === this.#hovered) return;
    this.#hovered = index;
    this.invalidate();
  }

  #onContextMenu(e: MouseEventArgs): void {
    const index = this.indexAt(e.x, e.y);
    if (index < 0) return;
    this.cancel(index);
    e.handled = true;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const stride = this.cellSize + this.gap;
    const font = this.font ?? theme.metrics.font;

    this.#entries.forEach((entry, i) => {
      const along = i * stride;
      const cell: Rect = {
        x: this.vertical ? 0 : along,
        y: this.vertical ? along : 0,
        width: this.cellSize,
        height: this.cellSize,
      };
      const hovered = i === this.#hovered;
      theme.drawQueueCell(rc, cell, { ...this.visualState(), hovered, head: i === 0 });

      const inset = 5;
      const icon: Rect = {
        x: cell.x + inset,
        y: cell.y + inset,
        width: cell.width - inset * 2,
        height: cell.height - inset * 2,
      };
      if (entry.sprite) rc.drawTexture(entry.sprite.texture, icon, entry.sprite.source);
      else if (entry.glyph) paintGlyph(entry.glyph, { rc, box: icon, ink: p.text, weight: 2 });
      else {
        const initial = entry.name.slice(0, 1).toUpperCase();
        const width = rc.measureText(initial, theme.metrics.captionFont).width;
        rc.drawText(
          initial,
          icon.x + (icon.width - width) / 2,
          icon.y + icon.height / 2,
          theme.metrics.captionFont,
          p.textSubtle,
          { baseline: "middle" },
        );
      }

      // Only the head is being worked on; the rest are simply waiting.
      if (i === 0 && this.headProgress > 0) {
        rc.fillArc(
          { x: cell.x + cell.width / 2, y: cell.y + cell.height / 2 },
          cell.width * 0.42,
          cell.width * 0.5,
          0,
          Math.PI * 2 * this.headProgress,
          p.accent,
        );
      }

      const count = entry.count ?? 1;
      if (count > 1) {
        const text = `x${count}`;
        const width = rc.measureText(text, font).width;
        rc.drawText(text, cell.x + cell.width - width - 3, cell.y + cell.height - 3, font, p.text, {
          baseline: "bottom",
        });
      }

      if (hovered && this.cancellable) {
        // A cross in the corner, so canceling is discoverable without
        // knowing that right-click does it.
        const size = 10;
        const box: Rect = { x: cell.x + cell.width - size - 2, y: cell.y + 2, width: size, height: size };
        rc.fillRect(box, withAlpha(p.desktop, 0.6), size / 2);
        paintGlyph("close", { rc, box, ink: p.text, weight: 1.5 });
      }
    });
  }
}
